/**
 * Due-date offset picker — the "Due date" widget from the compliance card,
 * shared by the compliance new/edit cards and the template item editor.
 *
 * "Custom" mode is explicit state, NOT derived from the value — the presets
 * list contains 1, so deriving mode from preset membership made Custom snap
 * back to "1 day" the instant it was selected (2026-10-06 bug).
 */

import { useState } from 'react';
import { fmtDate } from '../lib/date.js';

export const DUE_DATE_PRESETS = [1, 7, 14, 21, 28, 30, 37, 45];

export function formatDayLabel(n: number) {
  return n === 1 ? '1 day' : `${n} days`;
}

export function computeDueDate(endDate: string | null, offsetDays: number | null): string | null {
  if (!endDate || !offsetDays) return null;
  const d = new Date(endDate + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

export function DueDateOffsetField({ value, onChange, endDate, label = 'Due date', required = true }: {
  value: number | null;
  onChange: (v: number | null) => void;
  /** YYYY-MM-DD end date — when set, the computed due date is shown. */
  endDate?: string | null;
  label?: string;
  required?: boolean;
}) {
  const [customMode, setCustomMode] = useState(value != null && !DUE_DATE_PRESETS.includes(value));
  const [custom, setCustom] = useState<number | ''>(value != null && !DUE_DATE_PRESETS.includes(value) ? value : '');

  const dueDateDisplay = computeDueDate(endDate ?? null, value);

  return (
    <div>
      <label className="block text-xs text-muted mb-1">{label} {required && '*'}</label>
      <select
        value={customMode ? 'custom' : (value ?? '')}
        onChange={(e) => {
          const v = e.target.value;
          if (v === 'custom') {
            setCustomMode(true);
            // Force a deliberate number — never silently commit a preset.
            if (custom === '') onChange(null);
            else onChange(custom);
          } else if (v === '') {
            setCustomMode(false);
            onChange(null);
          } else {
            setCustomMode(false);
            onChange(Number(v));
          }
        }}
        className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
        required={required}
      >
        <option value="">— select —</option>
        {DUE_DATE_PRESETS.map((n) => <option key={n} value={n}>{formatDayLabel(n)}</option>)}
        <option value="custom">Custom</option>
      </select>
      <p className="text-xs text-muted mt-1">Days after the end date when work is due.</p>
      {dueDateDisplay && <p className="text-xs text-muted mt-1">Computed due date: {fmtDate(dueDateDisplay)}</p>}
      {customMode && (
        <div className="mt-2">
          <label className="block text-xs text-muted mb-1">Custom offset (days) *</label>
          <input
            type="number"
            min={1}
            max={365}
            value={custom}
            onChange={(e) => {
              const n = e.target.value ? Number(e.target.value) : '';
              setCustom(n);
              onChange(typeof n === 'number' && n >= 1 && n <= 365 ? n : null);
            }}
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
            required
          />
          <p className="text-xs text-muted mt-1">Whole number of days, 1–365.</p>
        </div>
      )}
    </div>
  );
}
